import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { getChurchServiceBySlug, getEvents, getAlbumPhotos, ChurchService, ChurchEvent, AlbumPhoto } from '../../services/api';
import { FaFileAlt, FaDownload } from 'react-icons/fa';

const PageContainer = styled.div`
  direction: rtl;
`;

const Banner = styled.div<{ image: string }>`
  background: linear-gradient(rgba(0,0,0,0.55), rgba(0,0,0,0.55)),
              url('${p => p.image}') center/cover no-repeat;
  height: 350px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  text-align: center;
  margin: -2rem -1rem 0;
`;

const BannerTitle = styled.h1`
  font-size: 2.8rem;
  color: #D4AF37;

  @media (max-width: 768px) {
    font-size: 1.8rem;
  }
`;

const Content = styled.div`
  max-width: 900px;
  margin: 3rem auto;
  padding: 0 1rem;
`;

const InfoCard = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0,0,0,0.1);
  padding: 2rem;
  margin-bottom: 2rem;
  border-right: 5px solid #8B0000;
`;

const InfoRow = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
  align-items: flex-start;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const InfoLabel = styled.span`
  color: #8B0000;
  font-weight: bold;
  min-width: 120px;
`;

const InfoValue = styled.span`
  color: #333;
  line-height: 1.6;
`;

const Description = styled.p`
  color: #444;
  line-height: 1.9;
  font-size: 1.1rem;
  white-space: pre-wrap;
`;

const SectionTitle = styled.h2`
  color: #8B0000;
  margin: 3rem 0 1.5rem;
  font-size: 1.8rem;
`;

const ScheduleTable = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  background: #8B0000;
  color: white;
  padding: 0.75rem;
  text-align: right;
`;

const Td = styled.td`
  padding: 0.75rem;
  border-bottom: 1px solid #eee;
`;

const NewsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.5rem;
`;

const NewsCard = styled(motion.div)`
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0,0,0,0.1);
  overflow: hidden;
`;

const NewsImage = styled.img`
  width: 100%;
  height: 180px;
  object-fit: cover;
`;

const NewsBody = styled.div`
  padding: 1.25rem;
`;

const NewsBadge = styled.span`
  display: inline-block;
  padding: 0.2rem 0.6rem;
  border-radius: 12px;
  font-size: 0.8rem;
  background: #E3F2FD;
  color: #1565C0;
  margin-bottom: 0.5rem;
`;

const NewsTitle = styled.h3`
  color: #8B0000;
  margin-bottom: 0.4rem;
`;

const NewsDate = styled.p`
  color: #D4AF37;
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
`;

const NewsDesc = styled.p`
  color: #555;
  line-height: 1.6;
`;

const GalleryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 1rem;
`;

const GalleryImage = styled.img`
  width: 100%;
  height: 150px;
  object-fit: cover;
  border-radius: 10px;
`;

const DownloadsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const DownloadItem = styled.a`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background: white;
  border-radius: 10px;
  padding: 1rem 1.25rem;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  color: #8B0000;
  text-decoration: none;
  font-weight: 500;
  transition: box-shadow 0.3s;
  &:hover { box-shadow: 0 4px 12px rgba(0,0,0,0.15); }
`;

const TYPE_LABELS: Record<string, string> = {
  NEWS: 'خبر',
  EVENT: 'فعالية',
  CONFERENCE: 'مؤتمر',
  TRIP: 'رحلة',
  OCCASION: 'مناسبة',
  BIRTHDAY: 'عيد ميلاد',
  ANNOUNCEMENT: 'إعلان',
};

const defaults: Record<string, Partial<ChurchService>> = {
  youth:       { name: 'اجتماع الشباب', schedule: 'الخميس 7:30 - 9:30 مساءً', supervisor: 'تحت رعاية ابونا انطونيوس', bannerImage: '/img/abo-sefen.jpg' },
  seniors:     { name: 'خدمة المسنين', schedule: 'الأربعاء 7:30 - 9:30 مساءً', supervisor: 'تحت رعاية ابونا إبراهيم', bannerImage: '/img/services/seniors-hero.jpg' },
  women:       { name: 'خدمة السيدات', schedule: 'الاثنين 6:00 - 8:00 مساءً', supervisor: 'تحت رعاية ابونا إبراهيم', bannerImage: '/img/women/banner.jpg' },
  kashafa:     { name: 'خدمة كشافة الأنبا بولا', schedule: 'الأربعاء 7:30 - 9:30 مساءً', supervisor: 'تحت رعاية ابونا إبراهيم', bannerImage: '/img/women/banner.jpg' },
  abosefen:    { name: 'خدمة ابو سيفين لاخوة الرب', schedule: 'الجمعة 7:30 - 9:30 مساءً', supervisor: 'تحت رعاية ابونا فيلوباتير', bannerImage: '/img/abo-sefen.jpg' },
  'bible-study': { name: 'درس الكتاب المقدس', schedule: 'السبت 7:00 - 8:00 مساءً', supervisor: 'تحت رعاية ابونا إرميا', bannerImage: '/img/women/banner.jpg' },
  preparation: { name: 'خدمة إعداد خدام', schedule: 'الجمعة 11:00 صباحاً - 1:00 ظهراً', supervisor: 'تحت رعاية ابونا إرميا حلمي', bannerImage: '/img/women/banner.jpg' },
  school:      { name: 'مدرسة الشمامسة', schedule: 'الجمعة 12:30 - 1:00 ظهراً', supervisor: 'تحت رعاية ابونا إرميا حلمي', bannerImage: '/img/women/banner.jpg' },
  random:      { name: 'خدمة المناطق العشوائية', schedule: 'السبت الثالث من كل شهر 9:30 صباحاً - 1:00 ظهراً', supervisor: 'تحت رعاية ابونا إرميا حلمي', bannerImage: '/img/women/banner.jpg' },
};

const ServicePage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [service, setService] = useState<Partial<ChurchService> | null>(null);
  const [loading, setLoading] = useState(true);
  const [feed, setFeed] = useState<ChurchEvent[]>([]);
  const [gallery, setGallery] = useState<AlbumPhoto[]>([]);
  const [downloads, setDownloads] = useState<AlbumPhoto[]>([]);

  useEffect(() => {
    if (!slug) { setLoading(false); return; }
    getChurchServiceBySlug(slug)
      .then(res => setService(res.data))
      .catch(() => setService(defaults[slug] || null))
      .finally(() => setLoading(false));
  }, [slug]);

  useEffect(() => {
    if (!service?.id) return;
    getEvents(undefined, service.id).then(res => setFeed(res.data)).catch(() => {});
    getAlbumPhotos(undefined, service.id).then(res => {
      setGallery(res.data.filter(p => (p.type || 'image') === 'image'));
      setDownloads(res.data.filter(p => p.type === 'file'));
    }).catch(() => {});
  }, [service?.id]);

  if (loading) return <PageContainer style={{ padding: '4rem', textAlign: 'center' }}>جاري التحميل...</PageContainer>;
  if (!service) return <PageContainer style={{ padding: '4rem', textAlign: 'center' }}>الصفحة غير موجودة</PageContainer>;

  return (
    <PageContainer>
      <Banner image={service.bannerImage || '/img/women/banner.jpg'}>
        <BannerTitle>{service.name}</BannerTitle>
      </Banner>

      <Content>
        <InfoCard>
          {service.schedule && (
            <InfoRow>
              <InfoLabel>الموعد:</InfoLabel>
              <InfoValue>{service.schedule}</InfoValue>
            </InfoRow>
          )}
          {service.supervisor && (
            <InfoRow>
              <InfoLabel>الإشراف:</InfoLabel>
              <InfoValue>{service.supervisor}</InfoValue>
            </InfoRow>
          )}
        </InfoCard>

        {service.description && <Description>{service.description}</Description>}
        {service.extraContent && <Description style={{ marginTop: '1rem' }}>{service.extraContent}</Description>}

        {!!service.schedules?.length && (
          <>
            <SectionTitle>مواعيد الاجتماعات الأسبوعية</SectionTitle>
            <ScheduleTable>
              <thead>
                <tr>
                  <Th>اليوم</Th>
                  <Th>الوقت</Th>
                  <Th>المكان</Th>
                </tr>
              </thead>
              <tbody>
                {service.schedules.map((s, i) => (
                  <tr key={i}>
                    <Td>{s.day}</Td>
                    <Td>{s.time}</Td>
                    <Td>{s.location || '-'}</Td>
                  </tr>
                ))}
              </tbody>
            </ScheduleTable>
          </>
        )}

        {!!feed.length && (
          <>
            <SectionTitle>الأخبار والفعاليات</SectionTitle>
            <NewsGrid>
              {feed.map((item, i) => (
                <NewsCard key={item.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}>
                  {item.image && <NewsImage src={item.image} alt={item.title} />}
                  <NewsBody>
                    <NewsBadge>{TYPE_LABELS[item.eventType] || item.eventType}</NewsBadge>
                    <NewsTitle>{item.title}</NewsTitle>
                    {item.date && <NewsDate>{item.date}</NewsDate>}
                    <NewsDesc>{item.description}</NewsDesc>
                  </NewsBody>
                </NewsCard>
              ))}
            </NewsGrid>
          </>
        )}

        {!!gallery.length && (
          <>
            <SectionTitle>ألبوم الصور</SectionTitle>
            <GalleryGrid>
              {gallery.map(photo => (
                <GalleryImage key={photo.id} src={photo.image} alt={photo.title} />
              ))}
            </GalleryGrid>
          </>
        )}

        {!!downloads.length && (
          <>
            <SectionTitle>ملفات للتحميل</SectionTitle>
            <DownloadsList>
              {downloads.map(file => (
                <DownloadItem key={file.id} href={file.image} target="_blank" rel="noopener noreferrer">
                  <FaFileAlt /> {file.title || 'ملف'} <FaDownload style={{ marginRight: 'auto' }} />
                </DownloadItem>
              ))}
            </DownloadsList>
          </>
        )}
      </Content>
    </PageContainer>
  );
};

export default ServicePage;
